import type { ListBuilderConfiguration } from '../../types';
import { allMerchandisersList } from './lists/all-merchandisers';
import { highTempMerchandisersList } from './lists/high-temp';
import { inactiveMerchandisersList } from './lists/inactive';
import { maintenanceMerchandisersList } from './lists/maintenance';
import { needsRefillMerchandisersList } from './lists/needs-refill';

/**
 * Merchandiser list configuration (PD-13923). Backed by the merchandiser
 * entity; the dedicated UI replaces the legacy location-based merchandiser list.
 *
 * Multi-view list (PD-13938) with 5 tabs — All / Needs Refill / Maintenance /
 * Inactive / High Temp — reborn from the legacy location-list merchandiser
 * tabs. Tabs apply their view via filterRules defaults (the vehicle pattern).
 */
export const MERCHANDISER_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		allMerchandisersList,
		needsRefillMerchandisersList,
		maintenanceMerchandisersList,
		inactiveMerchandisersList,
		highTempMerchandisersList,
	],
	paging: {
		defaultPageSize: 20,
		pageSizeOptions: [10, 20, 50, 100],
	},
	filters: {
		activeFilters: [
			'merchandiserSearch',
			'merchandiserStatus',
			'merchandiserPlacement',
			'merchandiserModel',
			'merchandiserFillLevel',
			'merchandiserHealth',
			'merchandiserHighTemp',
			'merchandiserRefillNeeded',
		],
		defaultFilters: {},
	},
	tableSorts: [
		{
			id: 'merchandiser-name',
			name: 'Name (A-Z)',
			fields: [
				{ direction: 'ASC', fieldName: 'name', objectTable: 'merchandiser' },
			],
			description: 'Sort merchandisers alphabetically by name',
		},
		{
			id: 'merchandiser-name-desc',
			name: 'Name (Z-A)',
			fields: [
				{ direction: 'DESC', fieldName: 'name', objectTable: 'merchandiser' },
			],
			description: 'Sort merchandisers reverse-alphabetically by name',
		},
		{
			id: 'status-asc',
			name: 'Status (A-Z)',
			fields: [
				{ direction: 'ASC', fieldName: 'status', objectTable: 'merchandiser' },
				{ direction: 'ASC', fieldName: 'name', objectTable: 'merchandiser' },
			],
			description: 'Group merchandisers by status, then by name',
		},
		// PD-16059 — rollup-derived sorts. The backend orders the whole
		// filtered set in memory (nulls last either way) before paginating.
		// Named to match the Fill % / Qty Needed column header sorts exactly,
		// so the dropdown shows one entry per direction and a header click
		// and a dropdown pick resolve to the same option.
		//
		// PD-16707 — these are also what keep both sortable now that the
		// default layout stacks Qty Needed under Fill % in one column, whose
		// header can only sort by Fill %. The All tab's default sort names
		// `fill-pct-asc` rather than a column, so it holds whether or not a
		// layout shows a Fill % column (with one, it resolves to that
		// column's header sort).
		{
			id: 'fill-pct-asc',
			name: 'Fill % (Low to High)',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'fillPercentage',
					objectTable: 'merchandiser',
				},
			],
			description: 'Emptiest merchandisers first; unmeasured units last',
		},
		{
			id: 'fill-pct-desc',
			name: 'Fill % (High to Low)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'fillPercentage',
					objectTable: 'merchandiser',
				},
			],
			description: 'Fullest merchandisers first; unmeasured units last',
		},
		// No direction in the name, so the dropdown offers it both ways
		// (High to Low and Low to High), as the Qty Needed header did.
		{
			id: 'quantity-needed',
			name: 'Qty Needed',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'quantityNeeded',
					objectTable: 'merchandiser',
				},
			],
			description: 'Merchandisers short the most units first',
		},
		// PD-16707 — the 90-Day Location Revenue column's two directions, named
		// to match its header sort for the same reason as Fill % above. Ordered
		// in memory server-side; warehouse and unplaced units (no customer
		// location) go last both ways.
		{
			id: 'location-revenue-desc',
			name: '90-Day Location Revenue (High to Low)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'locationRevenue90',
					objectTable: 'merchandiser',
				},
			],
			description: 'Highest-earning customer locations first',
		},
		{
			id: 'location-revenue-asc',
			name: '90-Day Location Revenue (Low to High)',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'locationRevenue90',
					objectTable: 'merchandiser',
				},
			],
			description: 'Lowest-earning customer locations first',
		},
	],
	objects: [
		{
			baseUrl: '/distribution/merchandisers/:id',
			objectLabel: 'Merchandiser',
			objectTable: 'merchandiser',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: 'name', fieldLabel: 'Name' },
				{ fieldName: 'manufacturer', fieldLabel: 'Manufacturer' },
				{ fieldName: 'model', fieldLabel: 'Model' },
				{ fieldName: 'year', fieldLabel: 'Year' },
				{ fieldName: 'status', fieldLabel: 'Status' },
				{ fieldName: 'locationInfo', fieldLabel: 'Placement' },
				// PD-16707 — the placement in parts (Customer Name column).
				{ fieldName: 'placementName', fieldLabel: 'Customer Name' },
				{ fieldName: 'placementAddress1', fieldLabel: 'Address 1' },
				{ fieldName: 'placementAddress2', fieldLabel: 'Address 2' },
				{ fieldName: 'placementCity', fieldLabel: 'City' },
				{ fieldName: 'placementState', fieldLabel: 'State' },
				{ fieldName: 'placementZip', fieldLabel: 'Zip' },
				{ fieldName: 'placementType', fieldLabel: 'Placement Type' },
				{ fieldName: 'clientId', fieldLabel: 'Client ID' },
				{ fieldName: 'warehouseId', fieldLabel: 'Warehouse ID' },
				{
					fieldName: 'locationRevenue90',
					fieldLabel: '90-Day Location Revenue',
				},
				{ fieldName: 'locationsCount', fieldLabel: 'Locations' },
				{ fieldName: 'itemsCount', fieldLabel: 'Items' },
				{ fieldName: 'totalQuantity', fieldLabel: 'On Hand' },
				{ fieldName: 'fillPercentage', fieldLabel: 'Fill %' },
				{ fieldName: 'quantityNeeded', fieldLabel: 'Qty Needed' },
				{ fieldName: 'stockLevel', fieldLabel: 'Stock' },
				{ fieldName: 'healthState', fieldLabel: 'Health' },
				// PD-16707 — offered so the single Sensor and Temp columns the
				// stacked layout replaced can be rebuilt from the picker, like
				// every other value the default layout now stacks.
				{ fieldName: 'sensorDeviceLabel', fieldLabel: 'Sensor' },
				{ fieldName: 'currentTemperature', fieldLabel: 'Temperature' },
				{ fieldName: 'alertSummary', fieldLabel: 'Alerts' },
				{ fieldName: 'createdDate', fieldLabel: 'Created Date' },
				{ fieldName: 'modifiedDate', fieldLabel: 'Modified Date' },
			],
		},
	],
};
